/**
 * <EstadoDaTela /> — carregando, sessão vencida, sem acesso e falha, num lugar só.
 *
 * É o jeito canônico de uma tela que busca dados mostrar três dos quatro estados
 * da regra 3 de interface (o quarto, vazio, é da própria tela, porque só ela sabe
 * dizer o que falta e o que fazer). Quem busca passa `carregando` e `erro`, e
 * recebe de volta o esqueleto, a orientação ou os próprios filhos. Se cada bloco
 * resolvesse isso do seu jeito, a mesma falha apareceria com frases diferentes na
 * mesma página.
 *
 * POR QUE 403 E 401 NÃO SÃO O MESMO ERRO. O cliente da API já separa os dois
 * (`nucleo/auth/cliente-api.ts`, `pedeEntrarDeNovo` e `pedeOutroCaminho`) porque a
 * saída é diferente: o 401 se resolve entrando de novo, o 403 não se resolve de
 * jeito nenhum por quem está olhando. Oferecer "Tentar de novo" num 403 seria
 * convidar a pessoa a bater na mesma porta até desistir — daí o padrão
 * `recusa="sem-acesso"`.
 */

import { type ReactElement, type ReactNode } from 'react';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { CAMINHO_ENTRADA } from '@/nucleo/auth/destino';
import { ErrorState, type ErrorStateAction } from '@/ui/ErrorState';
import { Skeleton } from '@/ui/base/skeleton';
import { CardSkeleton, TableSkeleton } from '@/ui/base/table-skeleton';

/**
 * A forma do esqueleto enquanto carrega — a do que vem depois, para a página não
 * saltar de altura quando o dado chega.
 *
 * - `'cartao'` (padrão): um cartão, para detalhe e painel.
 * - `'lista-de-cartoes'`: três cartões empilhados, para fila feita de `Card` e não
 *   de `DataTable` (ex.: Aprovações).
 * - `{ colunas }`: uma tabela com esse número de colunas.
 */
export type EsqueletoDaTela = 'cartao' | 'lista-de-cartoes' | { colunas: number };

/** Texto próprio para a recusa (403), quando a frase padrão de "sem acesso" não basta. */
export interface RecusaPropria {
  titulo?: string;
  descricao?: string;
  /** O caminho útil para quem foi recusado. Nunca "Tentar de novo". */
  acao?: ErrorStateAction;
}

/**
 * O que fazer com o 403.
 *
 * - `'sem-acesso'` (padrão): `ErrorState` "forbidden", sem botão de tentar de novo.
 * - `'tentar-de-novo'`: trata o 403 como falha comum, com "Tentar de novo".
 * - `{ titulo, descricao, acao }`: "forbidden" com texto e ação próprios, para a
 *   tela mais estreita que a área (ex.: o conector gov.br, só `ADMIN`, dentro de
 *   uma área que aceita outros papéis — `admin/integracoes/recusaDoConector.tsx`).
 */
export type RecusaDaTela = 'sem-acesso' | 'tentar-de-novo' | RecusaPropria;

export interface EstadoDaTelaProps {
  /** Primeira busca em andamento: ainda não há nada para mostrar. */
  carregando: boolean;
  /** Falha da busca, já traduzida pelo cliente da API. Nulo quando deu certo. */
  erro: unknown;
  /** Refaz a busca. É a ação do botão "Tentar de novo". */
  aoTentarDeNovo: () => void;
  /** O que está sendo carregado, para o leitor de tela e o título da falha. Ex.: "as inscrições". */
  descricao: string;
  /** Forma do esqueleto. Padrão: `'cartao'`. */
  esqueleto?: EsqueletoDaTela;
  /** O que fazer com o 403. Padrão: `'sem-acesso'`. */
  recusa?: RecusaDaTela;
  children: ReactNode;
}

export function EstadoDaTela({
  carregando,
  erro,
  aoTentarDeNovo,
  descricao,
  esqueleto = 'cartao',
  recusa = 'sem-acesso',
  children,
}: EstadoDaTelaProps): ReactElement {
  if (carregando) {
    // O texto só para leitor de tela diz o que está sendo carregado — quem não vê
    // a tela não vê o esqueleto.
    return (
      <div role="status" aria-live="polite">
        <Esqueleto forma={esqueleto} />
        <p className="sr-only">Carregando {descricao}.</p>
      </div>
    );
  }

  if (erro instanceof ErroApi && erro.pedeEntrarDeNovo) {
    // O cliente da API já tentou renovar o token por conta antes de desistir.
    // Chegar aqui significa que a renovação não bastou, então insistir na mesma
    // busca daria no mesmo.
    return (
      <ErrorState
        variant="session-expired"
        primaryAction={{ label: 'Entrar de novo', to: CAMINHO_ENTRADA }}
      />
    );
  }

  if (erro instanceof ErroApi && erro.pedeOutroCaminho && recusa !== 'tentar-de-novo') {
    if (recusa === 'sem-acesso') {
      return <ErrorState variant="forbidden" />;
    }
    return (
      <ErrorState
        variant="forbidden"
        title={recusa.titulo}
        description={recusa.descricao}
        primaryAction={recusa.acao}
      />
    );
  }

  if (erro) {
    return (
      <ErrorState
        variant="load-failed"
        title={`Não foi possível carregar ${descricao}`}
        // Nada de `technicalDetails`: número de situação, corpo da resposta e
        // identificador de correlação ficam no log, nunca na tela (CLAUDE.md,
        // "Regras de interface", item 8).
        primaryAction={{ label: 'Tentar de novo', onClick: aoTentarDeNovo }}
      />
    );
  }

  return <>{children}</>;
}

function Esqueleto({ forma }: { forma: EsqueletoDaTela }): ReactElement {
  if (forma === 'cartao') {
    return <CardSkeleton />;
  }
  if (forma === 'lista-de-cartoes') {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((indice) => (
          <div key={indice} className="space-y-2 rounded-2xl border p-4">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ))}
      </div>
    );
  }
  return <TableSkeleton columns={forma.colunas} />;
}
